import { Component, Input, OnInit } from '@angular/core';
import {
  CAPITAL_GAIN_LOCATION_LABELS,
  CAPITAL_GAIN_TYPE_KEY,
  CAPITAL_GAIN_TYPE_LABELS,
  INCOME_FORM_CAPITAL_GAIN_KEYS,
  IncomeFormCapitalGainKey,
  getCapitalGainFieldOrder,
} from '../../../../constants/income-from-capital-gain-constant';

export interface CapitalGainParticularGroup {
  type: 'text' | 'amount';
  fields: IncomeFormCapitalGainKey[];
}

@Component({
    selector: 'app-income-from-capital-gain-en-v2',
    templateUrl: './income-from-capital-gain-en-v2.component.html',
    styleUrls: [
        './income-from-capital-gain-en-v2.component.css',
        '../../../shared/return-view-text-fields.css',
    ],
    standalone: false
})
export class IncomeFromCapitalGainEnV2Component implements OnInit {
  @Input() returnData: any;
  @Input() printActive: boolean = false;

  incomeFormCapitalGainData: any[] = [];

  readonly gainTypeKey = CAPITAL_GAIN_TYPE_KEY;

  constructor() { }

  ngOnInit(): void {
    const capitalGainData = this.returnData?.incomeData?.capitalGainData;
    this.incomeFormCapitalGainData = (Array.isArray(capitalGainData) ? capitalGainData : [])
      .map((item) => this.flattenCapitalGainItem(item));
  }

  getValue(item: any, key: string): any {
    if (!item || !key) {
      return null;
    }
    if (key === '__capital_gain_based_on_sale_deed') {
      return this.toNumber(item.ICG_SALE_DEED_VALUE) - this.toNumber(item.ICG_COST_OF_ACQUISITION);
    }
    if (key === '__capital_gain_excess_amount') {
      return this.toNumber(item.ICG_DEED_EXCESS_AMT);
    }
    if (key === '__capital_gain_specific_contract') {
      return this.toNumber(item.ICG_COMPENSATION_AMOUNT) - this.toNumber(item.ICG_COST_OF_ACQUISITION);
    }
    if (key === '__capital_gain_tax') {
      const capitalGainOnContract = this.hasRawValue(item, 'ICG_CAPITAL_GAIN')
        ? this.toNumber(item.ICG_CAPITAL_GAIN)
        : (this.toNumber(item.ICG_COMPENSATION_AMOUNT) - this.toNumber(item.ICG_COST_OF_ACQUISITION));
      return Math.round(capitalGainOnContract * 0.15);
    }
    if (key === 'ICG_ONE_THIRD_PAY') {
      const raw = item[key];
      if (raw === true || String(raw).toLowerCase() === 'true') {
        return 'Yes';
      }
      if (raw === false || String(raw).toLowerCase() === 'false') {
        return 'No';
      }
      return null;
    }
    return item[key];
  }

  getGainTypeTitle(gainType: any): string {
    const v = gainType === null || gainType === undefined ? '' : String(gainType).trim();
    return CAPITAL_GAIN_TYPE_LABELS[v] || v;
  }

  getItemGainTypeTitle(item: any): string {
    const savedName = this.getValue(item, 'ICG_TYPE_OF_GAIN_NAME');
    if (savedName) {
      return String(savedName);
    }
    return this.getGainTypeTitle(this.getValue(item, this.gainTypeKey));
  }

  hasValue(item: any, key: string): boolean {
    const val = this.getValue(item, key);
    if (val === null || val === undefined || val === '') {
      return false;
    }
    if (key === '__capital_gain_based_on_sale_deed') {
      return this.hasRawValue(item, 'ICG_SALE_DEED_VALUE') || this.hasRawValue(item, 'ICG_COST_OF_ACQUISITION');
    }
    if (key === '__capital_gain_excess_amount') {
      return this.hasRawValue(item, 'ICG_DEED_EXCESS_AMT');
    }
    if (key === '__capital_gain_specific_contract' || key === '__capital_gain_tax') {
      return this.hasRawValue(item, 'ICG_COMPENSATION_AMOUNT')
        || this.hasRawValue(item, 'ICG_COST_OF_ACQUISITION')
        || this.hasRawValue(item, 'ICG_CAPITAL_GAIN');
    }
    if (key === 'ICG_ONE_THIRD_PAY') {
      return val === 'Yes' || val === 'No';
    }
    return true;
  }

  isApplicableForType(particular: IncomeFormCapitalGainKey, gainType: string): boolean {
    if (!particular.types || particular.types.length === 0) {
      return true;
    }
    return particular.types.includes(gainType);
  }

  getOrderedParticulars(item: any): IncomeFormCapitalGainKey[] {
    const gainType = String(this.getValue(item, this.gainTypeKey) || '').trim();
    return INCOME_FORM_CAPITAL_GAIN_KEYS
      .filter((k) =>
        !k.hideStandalone
        && k.key !== this.gainTypeKey
        && this.isApplicableForType(k, gainType)
        && this.hasValue(item, k.key)
      )
      .slice()
      .sort((a, b) =>
        getCapitalGainFieldOrder(gainType, a.key, a.order)
        - getCapitalGainFieldOrder(gainType, b.key, b.order)
      );
  }

  getParticularGroups(item: any): CapitalGainParticularGroup[] {
    const groups: CapitalGainParticularGroup[] = [];
    this.getOrderedParticulars(item).forEach((field) => {
      const last = groups[groups.length - 1];
      if (last && last.type === field.type) {
        last.fields.push(field);
        return;
      }
      groups.push({ type: field.type, fields: [field] });
    });
    return groups;
  }

  getTextFieldDisplayValue(item: any, field: IncomeFormCapitalGainKey): string {
    const value = this.getValue(item, field.key);
    if (field.key === this.gainTypeKey) {
      const savedName = this.getValue(item, 'ICG_TYPE_OF_GAIN_NAME');
      if (savedName) {
        return String(savedName);
      }
      return this.getGainTypeTitle(value);
    }
    if (field.key === 'bankName' && value && typeof value === 'object') {
      return String(value.bankName || value.name || '');
    }
    if (field.key === 'ICG_LAND_AREA_LOCATION') {
      const code = value === null || value === undefined ? '' : String(value).trim();
      return CAPITAL_GAIN_LOCATION_LABELS[code] || code;
    }
    if (field.unitKey) {
      const unit = this.getValue(item, field.unitKey);
      const amountPart = value === null || value === undefined ? '' : String(value).trim();
      const unitPart = unit === null || unit === undefined ? '' : String(unit).trim();
      if (amountPart && unitPart) {
        return `${amountPart} ${unitPart}`;
      }
      return amountPart || unitPart;
    }
    return String(value ?? '');
  }

  private hasRawValue(item: any, key: string): boolean {
    const val = item ? item[key] : null;
    return val !== null && val !== undefined && val !== '';
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const n = Number(String(value).replace(/,/g, ''));
    return isNaN(n) ? 0 : n;
  }

  private flattenCapitalGainItem(element: any): any {
    if (!element) {
      return {};
    }
    if (element.detailsEntities) {
      return Object.assign({}, element, element.detailsEntities);
    }
    return element;
  }
}
